import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Toaster, type ToastPosition } from 'react-hot-toast'
import App from './App.tsx'
import { ConfirmProvider } from './components/ConfirmProvider'
import { ProductModeProvider } from './contexts/ProductModeContext'
import { ensurePersistentAuthToken } from './utils/authStorage'
import { installSilentTokenRefreshListeners } from './utils/silentTokenRefresh'
import './utils/deferredInstallPrompt'
import './i18n'
import './index.css'

ensurePersistentAuthToken()
installSilentTokenRefreshListeners()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Determine basename based on environment
const getBasename = () => {
  // For local development, always use empty basename
  if (process.env.NODE_ENV === 'development') {
    console.log('🔧 Development mode: using empty basename')
    return ''
  }
  
  // For DigitalOcean hosting, always use empty basename
  // No subdirectory needed since we're hosting directly on the domain
  console.log('🔧 DigitalOcean production mode: using empty basename')
  return ''
}

/** Bottom on mobile so add/remove toasts don't cover the Messages topbar. */
function AppToaster() {
  const [position, setPosition] = useState<ToastPosition>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches
      ? 'bottom-center'
      : 'top-right'
  )

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)')
    const apply = () => setPosition(mq.matches ? 'bottom-center' : 'top-right')
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  return (
    <Toaster
      position={position}
      gutter={8}
      toastOptions={{
        duration: 4000,
        style: {
          background: '#363636',
          color: '#fff',
        },
        success: {
          iconTheme: {
            primary: '#4e6ba6',
            secondary: '#fff',
          },
        },
      }}
    />
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={getBasename()}>
        <ProductModeProvider>
          <ConfirmProvider>
            <App />
          </ConfirmProvider>
        </ProductModeProvider>
        <AppToaster />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
